import React, { useState, useRef } from 'react';
import Modal from './Modal';
import api from '../services/api';
import * as XLSX from 'xlsx';

// Mapeo flexible de columnas — acepta variaciones del nombre
const COLUMNAS_MAP = {
  nombre:                   ['nombre', 'nombre completo', 'name', 'cliente'],
  cedula:                   ['cedula', 'cédula', 'cc', 'documento', 'identificacion', 'identificación'],
  telefono:                 ['telefono', 'teléfono', 'tel', 'celular', 'phone'],
  email:                    ['email', 'correo', 'correo electrónico', 'mail'],
  direccion:                ['direccion', 'dirección', 'address'],
  plan:                     ['plan', 'plan (nombre exacto) *', 'plan (nombre exacto)', 'suscripcion', 'suscripción'],
  dia_pago:                 ['dia_pago', 'día de pago (1-28)', 'dia de pago', 'día pago', 'dia pago'],
  estado:                   ['estado', 'status'],
  fecha_instalacion:        ['fecha_instalacion', 'fecha instalación', 'fecha instalacion', 'fecha de instalación', 'fecha de instalacion'],
  observaciones_instalacion:['observaciones_instalacion', 'notas instalación', 'notas instalacion', 'notas de instalación'],
  notas:                    ['notas', 'notas generales', 'observaciones', 'comentarios'],
};

const normalizarClave = (header) => {
  const h = header?.toString().toLowerCase().trim();
  for (const [campo, variantes] of Object.entries(COLUMNAS_MAP)) {
    if (variantes.includes(h)) return campo;
  }
  return null;
};

export default function ImportarClientes({ abierto, onCerrar, onImportado }) {
  const [paso, setPaso] = useState(1); // 1=subir, 2=previsualizar, 3=resultado
  const [clientes, setClientes] = useState([]);
  const [resultado, setResultado] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [errorArchivo, setErrorArchivo] = useState('');
  const inputRef = useRef(null);

  const resetear = () => { setPaso(1); setClientes([]); setResultado(null); setErrorArchivo(''); };

  const onCerrarModal = () => { resetear(); onCerrar(); };

  const procesarArchivo = (archivo) => {
    setErrorArchivo('');
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary', cellDates: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const filas = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

        // Encontrar fila de encabezados (buscar la que tenga "nombre")
        let filaHeader = -1;
        for (let i = 0; i < Math.min(filas.length, 10); i++) {
          const fila = filas[i].map(c => c?.toString().toLowerCase().trim());
          if (fila.some(c => ['nombre', 'nombre completo', 'name'].includes(c))) {
            filaHeader = i;
            break;
          }
        }

        if (filaHeader === -1) {
          setErrorArchivo('No se encontró la fila de encabezados. Asegúrate de usar la plantilla descargada.');
          return;
        }

        const headers = filas[filaHeader].map(h => normalizarClave(h));
        const datos = [];

        for (let i = filaHeader + 1; i < filas.length; i++) {
          const fila = filas[i];
          if (fila.every(c => c === '' || c === null || c === undefined)) continue;

          const obj = {};
          headers.forEach((campo, idx) => {
            if (campo) {
              let val = fila[idx];
              if (val instanceof Date) {
                // Formatear fecha como YYYY-MM-DD
                val = val.toISOString().split('T')[0];
              } else {
                val = val?.toString().trim() || '';
              }
              obj[campo] = val;
            }
          });

          if (obj.nombre) datos.push(obj);
        }

        if (datos.length === 0) {
          setErrorArchivo('No se encontraron clientes en el archivo. Verifica que hayas llenado los datos.');
          return;
        }

        setClientes(datos);
        setPaso(2);
      } catch (err) {
        setErrorArchivo('Error al leer el archivo. Asegúrate de que sea un Excel válido (.xlsx o .xls).');
      }
    };
    reader.readAsBinaryString(archivo);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const archivo = e.dataTransfer.files[0];
    if (archivo) procesarArchivo(archivo);
  };

  const importar = async () => {
    setCargando(true);
    try {
      const { data } = await api.post('/api/importar/clientes', { clientes });
      setResultado(data);
      setPaso(3);
      onImportado();
    } catch (err) {
      setErrorArchivo(err.response?.data?.error || 'Error al importar');
    } finally { setCargando(false); }
  };

  return (
    <Modal abierto={abierto} onCerrar={onCerrarModal} titulo="Importar Clientes desde Excel" ancho="640px">

      {/* Paso 1: Subir archivo */}
      {paso === 1 && (
        <div>
          <div style={s.pasos}>
            {['Subir archivo', 'Previsualizar', 'Resultado'].map((p, i) => (
              <div key={i} style={{ ...s.paso, ...(paso === i+1 ? s.pasoActivo : paso > i+1 ? s.pasoListo : {}) }}>
                <span style={s.pasoNum}>{paso > i+1 ? '✓' : i+1}</span>
                <span>{p}</span>
              </div>
            ))}
          </div>

          <div style={s.instrucciones}>
            <div style={s.instruccionTitulo}>📋 Instrucciones</div>
            <div style={s.instruccionItem}>1. Descarga la plantilla Excel con el botón de abajo (ya la tienes si la descargaste antes)</div>
            <div style={s.instruccionItem}>2. Llena los datos de tus clientes (una fila por cliente)</div>
            <div style={s.instruccionItem}>3. Guarda el archivo y súbelo aquí</div>
            <div style={s.instruccionItem}>💡 También puedes subir tu propio Excel si las columnas tienen nombres similares</div>
          </div>

          <a
            href="data:application/octet-stream;base64,"
            id="dl-plantilla"
            style={{ display: 'none' }}
          />
          <button style={s.btnPlantilla} onClick={() => {
            fetch('http://localhost:4000/api/importar/plantilla')
              .then(r => r.ok ? r.blob() : Promise.reject('no encontrado'))
              .then(blob => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = 'plantilla_clientes_telecom.xlsx';
                a.click(); URL.revokeObjectURL(url);
              })
              .catch(() => alert('Descarga la plantilla directamente desde el chat donde fue generada.'));
          }}>
            ⬇ Descargar plantilla Excel
          </button>

          <div
            style={s.dropzone}
            onDrop={onDrop}
            onDragOver={e => e.preventDefault()}
            onClick={() => inputRef.current?.click()}
          >
            <div style={s.dropIcon}>📂</div>
            <div style={s.dropTitulo}>Arrastra tu archivo aquí o haz clic para seleccionar</div>
            <div style={s.dropSub}>Formatos aceptados: .xlsx, .xls, .csv</div>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              style={{ display: 'none' }}
              onChange={e => { if (e.target.files[0]) procesarArchivo(e.target.files[0]); }}
            />
          </div>

          {errorArchivo && <div style={s.error}>⚠ {errorArchivo}</div>}
        </div>
      )}

      {/* Paso 2: Previsualizar */}
      {paso === 2 && (
        <div>
          <div style={s.pasos}>
            {['Subir archivo', 'Previsualizar', 'Resultado'].map((p, i) => (
              <div key={i} style={{ ...s.paso, ...(paso === i+1 ? s.pasoActivo : paso > i+1 ? s.pasoListo : {}) }}>
                <span style={s.pasoNum}>{paso > i+1 ? '✓' : i+1}</span><span>{p}</span>
              </div>
            ))}
          </div>

          <div style={s.resumenPrev}>
            <span style={s.resumenNum}>{clientes.length}</span>
            <span style={s.resumenLabel}>clientes detectados en el archivo</span>
          </div>

          <div style={s.tablaPreview}>
            <table style={s.table}>
              <thead>
                <tr style={s.thead}>
                  <th style={s.th}>#</th>
                  <th style={s.th}>Nombre</th>
                  <th style={s.th}>Cédula</th>
                  <th style={s.th}>Teléfono</th>
                  <th style={s.th}>Plan</th>
                  <th style={s.th}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {clientes.slice(0, 10).map((c, i) => (
                  <tr key={i} style={s.tr}>
                    <td style={s.td}>{i + 1}</td>
                    <td style={s.td}><strong>{c.nombre}</strong></td>
                    <td style={s.td}>{c.cedula || '—'}</td>
                    <td style={s.td}>{c.telefono || '—'}</td>
                    <td style={s.td}>{c.plan || <span style={{ color:'rgba(255,255,255,0.3)' }}>Sin plan</span>}</td>
                    <td style={s.td}><span style={{ fontSize:'11px', textTransform:'capitalize' }}>{c.estado || 'activo'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {clientes.length > 10 && (
              <div style={s.masClientes}>... y {clientes.length - 10} clientes más</div>
            )}
          </div>

          {errorArchivo && <div style={s.error}>⚠ {errorArchivo}</div>}

          <div style={s.modalFooter}>
            <button style={s.btnSec} onClick={() => { setPaso(1); setClientes([]); }}>← Volver</button>
            <button style={s.btnPrimario} onClick={importar} disabled={cargando}>
              {cargando ? 'Importando...' : `✓ Importar ${clientes.length} clientes`}
            </button>
          </div>
        </div>
      )}

      {/* Paso 3: Resultado */}
      {paso === 3 && resultado && (
        <div>
          <div style={s.resultadoBox}>
            <div style={s.resultadoIcono}>{resultado.fallidos === 0 ? '🎉' : '⚠'}</div>
            <div style={s.resultadoMensaje}>{resultado.mensaje}</div>
            <div style={s.resultadoStats}>
              <div style={s.rStat}><span style={{ color:'#10B981', fontSize:'24px', fontWeight:'700' }}>{resultado.exitosos}</span><span>importados</span></div>
              {resultado.fallidos > 0 && <div style={s.rStat}><span style={{ color:'#EF4444', fontSize:'24px', fontWeight:'700' }}>{resultado.fallidos}</span><span>con error</span></div>}
            </div>
          </div>

          {resultado.errores?.length > 0 && (
            <div style={s.erroresBox}>
              <div style={s.erroresTitulo}>Detalle de errores:</div>
              {resultado.errores.map((e, i) => <div key={i} style={s.errorItem}>• {e}</div>)}
            </div>
          )}

          <div style={s.modalFooter}>
            <button style={s.btnPrimario} onClick={onCerrarModal}>✓ Listo</button>
          </div>
        </div>
      )}
    </Modal>
  );
}

const s = {
  pasos: { display:'flex', gap:'8px', marginBottom:'20px' },
  paso: { display:'flex', alignItems:'center', gap:'6px', padding:'6px 12px', borderRadius:'20px', fontSize:'12px', color:'rgba(255,255,255,0.4)', background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)' },
  pasoActivo: { color:'#00E5FF', background:'rgba(0,229,255,0.1)', border:'1px solid rgba(0,229,255,0.3)' },
  pasoListo: { color:'#10B981', background:'rgba(16,185,129,0.1)', border:'1px solid rgba(16,185,129,0.3)' },
  pasoNum: { width:'18px', height:'18px', borderRadius:'50%', background:'rgba(255,255,255,0.1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'11px', fontWeight:'700' },
  instrucciones: { background:'rgba(99,102,241,0.06)', border:'1px solid rgba(99,102,241,0.2)', borderRadius:'10px', padding:'14px 16px', marginBottom:'16px' },
  instruccionTitulo: { fontSize:'13px', fontWeight:'600', color:'#A5B4FC', marginBottom:'8px' },
  instruccionItem: { fontSize:'12px', color:'rgba(255,255,255,0.6)', lineHeight:'1.8' },
  btnPlantilla: { width:'100%', background:'rgba(16,185,129,0.1)', border:'1px solid rgba(16,185,129,0.25)', borderRadius:'10px', padding:'10px', color:'#6EE7B7', fontSize:'13px', cursor:'pointer', fontWeight:'600', marginBottom:'14px' },
  dropzone: { border:'2px dashed rgba(255,255,255,0.15)', borderRadius:'12px', padding:'32px', textAlign:'center', cursor:'pointer', transition:'all 0.2s', marginBottom:'14px' },
  dropIcon: { fontSize:'36px', marginBottom:'10px' },
  dropTitulo: { fontSize:'14px', fontWeight:'600', color:'rgba(255,255,255,0.8)', marginBottom:'6px' },
  dropSub: { fontSize:'12px', color:'rgba(255,255,255,0.4)' },
  error: { background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'8px', padding:'10px', color:'#FCA5A5', fontSize:'13px', marginBottom:'12px' },
  resumenPrev: { display:'flex', alignItems:'center', gap:'12px', background:'rgba(0,229,255,0.06)', border:'1px solid rgba(0,229,255,0.2)', borderRadius:'10px', padding:'14px 16px', marginBottom:'16px' },
  resumenNum: { fontSize:'32px', fontWeight:'800', color:'#00E5FF' },
  resumenLabel: { fontSize:'14px', color:'rgba(255,255,255,0.6)' },
  tablaPreview: { background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'10px', overflow:'hidden', marginBottom:'14px', maxHeight:'280px', overflowY:'auto' },
  table: { width:'100%', borderCollapse:'collapse' },
  thead: { background:'rgba(255,255,255,0.06)', position:'sticky', top:0 },
  th: { padding:'9px 12px', textAlign:'left', fontSize:'11px', color:'rgba(255,255,255,0.4)', fontWeight:'600', textTransform:'uppercase' },
  tr: { borderTop:'1px solid rgba(255,255,255,0.05)' },
  td: { padding:'10px 12px', fontSize:'12px' },
  masClientes: { padding:'10px 16px', fontSize:'12px', color:'rgba(255,255,255,0.4)', textAlign:'center', borderTop:'1px solid rgba(255,255,255,0.06)' },
  resultadoBox: { textAlign:'center', padding:'24px', background:'rgba(255,255,255,0.03)', borderRadius:'12px', marginBottom:'16px' },
  resultadoIcono: { fontSize:'40px', marginBottom:'12px' },
  resultadoMensaje: { fontSize:'14px', color:'rgba(255,255,255,0.7)', marginBottom:'16px' },
  resultadoStats: { display:'flex', gap:'24px', justifyContent:'center' },
  rStat: { display:'flex', flexDirection:'column', alignItems:'center', gap:'4px', fontSize:'13px', color:'rgba(255,255,255,0.5)' },
  erroresBox: { background:'rgba(239,68,68,0.06)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'8px', padding:'12px 16px', marginBottom:'16px', maxHeight:'160px', overflowY:'auto' },
  erroresTitulo: { fontSize:'12px', fontWeight:'600', color:'#FCA5A5', marginBottom:'8px' },
  errorItem: { fontSize:'12px', color:'rgba(255,255,255,0.6)', lineHeight:'1.8' },
  btnPrimario: { background:'linear-gradient(135deg,#00E5FF,#6366F1)', border:'none', borderRadius:'10px', padding:'10px 20px', color:'#fff', fontSize:'14px', fontWeight:'600', cursor:'pointer' },
  btnSec: { background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'10px', padding:'10px 18px', color:'#fff', fontSize:'14px', cursor:'pointer' },
  modalFooter: { display:'flex', gap:'12px', justifyContent:'flex-end' },
};
